import { once } from 'lodash-es';
import moment from 'moment';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { useDialogControls } from '../common/utils/dialogUtils';
import Whitespace from '../components/Whitespace';
import CollectDebugReportButton from '../components/debug/CollectDebugReportButton';
import { InlineLink } from '../components/typography';
import { config } from '../config/config';
import { UserControlledChangelogDialog } from '../dialogs/ChangelogDialog';
import { useMetadata } from '../hooks/data/misc';
import { log } from '../utils/logger';
import { useMetadataWidgetsStyles } from './metadataWidgets.styles';

const VersionNumberLabel = ({ title }) => (
  <span title={title}>
    <FormattedMessage id="labels.version" />
    <Whitespace />
    {config.version}
  </span>
);

function VersionNumberWithLink({ title }) {
  const { isOpen, open, close } = useDialogControls();
  return (
    <>
      <UserControlledChangelogDialog isOpen={isOpen} close={close} />
      <InlineLink onClick={open} data-subject="changelog" data-action="open">
        <VersionNumberLabel title={title} />
      </InlineLink>
    </>
  );
}

export function VersionNumber({ canOpenChangelog, title }) {
  return canOpenChangelog ? (
    <VersionNumberWithLink title={title} />
  ) : (
    <VersionNumberLabel title={title} />
  );
}

function MockInfo({ metadata }) {
  return metadata?.isMock ? ' (MOCK)' : null;
}

function DebugInfo({ canOpenChangelog }) {
  const date = moment
    .unix(config.commitTimestamp)
    .format('YYYY-MM-DD HH:mm UTC');
  return (
    <div>
      <CollectDebugReportButton />
      <Whitespace />
      <VersionNumber canOpenChangelog={canOpenChangelog} title={date} />
    </div>
  );
}

const logAppInfoOnce = once(metadata =>
  log.info('App info', {
    version: config.version,
    mock: metadata.isMock,
    logLevel: config.logLevel,
    commitHash: config.commitHash,
    commitTimestamp: config.commitTimestamp,
  })
);

export function MetadataInfo({ canOpenChangelog }) {
  const { styles } = useMetadataWidgetsStyles();
  const metadata = useMetadata();
  useEffect(() => {
    if (!metadata) {
      return;
    }
    logAppInfoOnce(metadata);
  }, [metadata]);
  return (
    <div className={styles.metadataInfo}>
      <MockInfo metadata={metadata} />
      {config.showDebugInfo ? (
        <DebugInfo canOpenChangelog={canOpenChangelog} />
      ) : (
        <VersionNumber canOpenChangelog={canOpenChangelog} />
      )}
    </div>
  );
}
